import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { User } from '@restore/services-lib';
import jwt_decode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { LOGIN_LANDING_PAGE } from 'const/const';
import { AppAbilityInterface, generateAbility, newAbility } from 'config/ability';
import { AbilityContext } from 'components/Abilities/Can';

export interface UserInterface {
  caslAbilities?: string;
  user: User;
  tokens: {
    id_token: string;
    access_token: string;
    refresh_token: string;
  };
}

interface AuthContextInterface {
  user?: UserInterface;
  setUser: (user: UserInterface) => void;
  login: (user: UserInterface, loginDestination?: string) => void;
  userTenantId?: number;
  userTenantIdOnLogin?: number;
  setUserTenantId: (tenantId?: number) => void;
  isSessionActive: () => boolean;
  logout: () => void;
  ability: AppAbilityInterface;
  permissionsLoaded: boolean;
}

const defaultContext: AuthContextInterface = {
  setUser: () => {},
  setUserTenantId: () => {},
  login: () => {},
  isSessionActive: () => true,
  logout: () => {},
  ability: newAbility(),
  permissionsLoaded: false,
};

const AuthContext = createContext<AuthContextInterface>(defaultContext);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserInterface>();
  const [userTenantId, setUserTenantId] = useState<number | undefined>(user?.user.tenantId);
  const [userTenantIdOnLogin, setUserTenantIdOnLogin] = useState<number | undefined>(
    user?.user.tenantId
  );
  const navigate = useNavigate();
  const [ability, setAbility] = useState<AppAbilityInterface>(newAbility());
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);

  const updateUserTenantId = (newTenantId?: number) => {
    if (newTenantId) {
      localStorage.setItem('restore.user.lastViewedTenantId', newTenantId.toString());
      setUserTenantId(newTenantId);
    }
  };
  
  const updateUserTenantIdOnLogin = (newTenantId?: number) => {
    if (newTenantId) {
      localStorage.setItem('restore.user.originalTenantId', newTenantId.toString());
      setUserTenantIdOnLogin(newTenantId);
    }
  };
  
  const restoreTenantIdsFromLocalStorage = (localStorageUser: UserInterface) => {
    const localStorageLastViewedTenantId = localStorage.getItem('restore.user.lastViewedTenantId');
    if (localStorageLastViewedTenantId !== null) {
      localStorageUser.user.tenantId = parseInt(localStorageLastViewedTenantId, 10);
      setUserTenantId(localStorageUser.user.tenantId);
    }

    const localStorageUserTenantIdOnLogin = localStorage.getItem('restore.user.originalTenantId');
    if (localStorageUserTenantIdOnLogin !== null) {
      setUserTenantIdOnLogin(parseInt(localStorageUserTenantIdOnLogin, 10));
    }
  };

  useEffect(() => {
    setUserTenantId(user?.user.tenantId);
  }, [user?.user.tenantId]);

  const restoreSession = useCallback(() => {
    if (isSessionActive()) {
      const localStorageUserJson = localStorage.getItem('restore.user');
      const localStorageUser = localStorageUserJson && JSON.parse(localStorageUserJson);
      setUser(localStorageUser);
      setAbility(generateAbility(localStorageUser));
      setPermissionsLoaded(true);
      restoreTenantIdsFromLocalStorage(localStorageUser);
    } else {
      logout();
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  const login = (user: UserInterface, loginDestination?: string) => {
    localStorage.setItem('restore.user', JSON.stringify(user));
    updateUserTenantIdOnLogin(user.user.tenantId);
    setUser(user);
    setAbility(generateAbility(user));
    setPermissionsLoaded(true);

    if (loginDestination) {
      navigate(loginDestination);
    } else {
      navigate(LOGIN_LANDING_PAGE);
    }
  };

  const isSessionActive = () => {
    const localStorageUserJson = localStorage.getItem('restore.user');
    let isActive = false;

    if (localStorageUserJson) {
      const localStorageUser = JSON.parse(localStorageUserJson);
      const decodedToken: { exp: number } = jwt_decode(localStorageUser.tokens.access_token ?? '');
      const expireTime = Number(decodedToken.exp) * 1000;
      isActive = decodedToken && expireTime > Date.now();
    }

    return isActive;
  };

  const logout = () => {
    if (localStorage.getItem('restore.user')) {
      localStorage.removeItem('restore.user');
      localStorage.removeItem('restore.user.lastViewedTenantId');
      localStorage.removeItem('restore.user.originalTenantId');
      setUser(undefined);
      setAbility(newAbility);
      setPermissionsLoaded(false);
      window.location.replace('/login'); // good here to reset all contexts
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        userTenantId,
        userTenantIdOnLogin,
        setUserTenantId: updateUserTenantId,
        login,
        isSessionActive,
        logout,
        ability,
        permissionsLoaded,
      }}
    >
      <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
